import { useCallback, useEffect, useState } from "react";
import { Phone, Mail, MessageCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtLongDate } from "@/lib/format";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MarketingContacts, waPhone } from "@/components/MarketingContacts";
import { ClientsList } from "@/components/ClientsList";

function LeadRow({ l, i, onDelete }) {
  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`lead-row-${i}`}>
      <div>
        <p className="font-semibold text-slate-900">{l.first_name} {l.last_name}
          <span className="ml-2 text-xs font-normal text-slate-500">{l.kind_label}{l.equipment_type ? ` · ${l.equipment_type}` : ""}</span></p>
        <p className="mt-0.5 flex flex-wrap gap-x-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{l.phone}</span>
          <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{l.email}</span>
          <span>{[l.address, l.city].filter(Boolean).join(", ")}</span>
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {l.date ? `Data scelta: ${fmtLongDate(l.date)} ${l.start || ""} · ` : ""}Ultima attività: {new Date(l.updated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
        </p>
      </div>
      <div className="flex gap-2">
        <a href={`https://wa.me/${waPhone(l.phone)}`} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-${i}`}>
          <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5a]"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
        </a>
        <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => onDelete(l)} data-testid={`lead-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const load = useCallback(() => api.get("/admin/leads").then((r) => setLeads(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (l) => {
    if (!window.confirm(`Eliminare il contatto di ${l.first_name} ${l.last_name}?`)) return;
    try {
      await api.delete(`/admin/leads/${l.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Contatti e consensi" title="Contatti" />
      <Tabs defaultValue="clients">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="clients" data-testid="contacts-tab-clients">Clienti</TabsTrigger>
          <TabsTrigger value="leads" data-testid="contacts-tab-leads">Prenotazioni non completate</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="contacts-tab-marketing">Consenso marketing</TabsTrigger>
        </TabsList>
        <TabsContent value="leads">
      <div className="divide-y rounded-2xl border bg-white" data-testid="leads-list">
        {leads === null ? <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          : leads.length === 0 ? <p className="p-6 text-center text-sm text-slate-500" data-testid="leads-empty">Nessun contatto in sospeso.</p>
          : leads.map((l, i) => <LeadRow key={l.id} l={l} i={i} onDelete={remove} />)}
      </div>
      <p className="mt-3 text-xs text-slate-500">Clienti che hanno inserito i propri dati ma non hanno inviato la richiesta. Quando completano la prenotazione spariscono da qui.</p>
        </TabsContent>
        <TabsContent value="clients"><ClientsList /></TabsContent>
        <TabsContent value="marketing"><MarketingContacts /></TabsContent>
      </Tabs>
    </div>
  );
}
